import React, {useEffect, useRef} from 'react';
import {View, StyleSheet, TouchableOpacity, Platform} from 'react-native';
import {useStyles} from '../../hooks/styles';
import {IBaseModalProps} from './model';
import {images} from '../../../assets/icons';
import {stylePatterns} from '../../theme/patterns';
import {BackHandler} from 'react-native';
import {Text} from '../text';
import {useTheme} from '../../theme';

export const BaseModal: React.FC<IBaseModalProps> = ({
  children,
  rootStyle,
  overlayStyle,
  allowClicksThroughOverlay,
  wrapStyle,
  titleStyle,
  title,
  onClose,
  contentStyle,
  customContent,
  hideCloseButton = false,
}) => {
  const wrapRef = useRef(null);

  useEffect(() => {
    const onBackPress = () => {
      if (onClose) {
        onClose();
        return true;
      }
      return false;
    };
    const subscription = BackHandler.addEventListener('hardwareBackPress', onBackPress);

    return () => subscription.remove();
  }, [onClose]);

  const {Close} = images;
  const theme = useTheme();
  const styles = useStyles(() => ({
    modal: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 100,
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },
    background: {
      position: 'absolute',
      top: 64,
      left: 0,
      right: 0,
      bottom: 0,
    },
    overlay: {
      ...stylePatterns.flexCenterFull,
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      margin: 'auto',
    },
    wrap: {
      ...theme.modal.wrap,
      zIndex: 1,
      opacity: 1,
      borderRadius: 5,
      paddingVertical: 40,
      paddingHorizontal: 150,
    },
    cross: {
      ...stylePatterns.flexCenterFull,
      position: 'absolute',
      top: 20,
      right: 20,
      width: 24,
      height: 24,
      zIndex: 2,
    },
    topBar: {},
    title: {
      textAlign: 'center',
      paddingTop: 20,
    },
    content: {
      paddingTop: 50,
    },
    inner: {
      margin: 'auto',
    },
  }));

  const desktopContentStyle = Platform.OS === 'web' ? {wordWrap: 'break-word', width: '100%'} : null;

  return (
    <View
      {...{dataSet: {class: 'modal-window', ...(allowClicksThroughOverlay ? {'pointer-events': 'none'} : {})}}}
      style={[StyleSheet.absoluteFill, styles.modal, rootStyle]}>
      <View
        {...{dataSet: {...(allowClicksThroughOverlay ? {'pointer-events': 'none'} : {})}}}
        style={[styles.overlay, overlayStyle]}>
        <View {...{dataSet: {...(allowClicksThroughOverlay ? {'pointer-events': 'none'} : {})}}} style={styles.inner}>
          {customContent || (
            <View
              ref={wrapRef}
              style={[styles.wrap, wrapStyle]}
              {...{dataSet: {...(allowClicksThroughOverlay ? {'pointer-events': 'auto'} : {})}}}>
              {onClose && !hideCloseButton ? (
                <TouchableOpacity testID='modal-close-id' activeOpacity={1} style={styles.cross} onPress={onClose}>
                  <Close color={theme.color.textColor} width={24} height={24} />
                </TouchableOpacity>
              ) : null}
              <View style={styles.topBar}>
                {title ? (
                  <Text testID='modal-title-id' type='h3Light' style={[styles.title, titleStyle]}>
                    {title}
                  </Text>
                ) : null}
              </View>
              <View style={[styles.content, desktopContentStyle, contentStyle]}>{children}</View>
            </View>
          )}
        </View>
      </View>
    </View>
  );
};
